import axios from "axios";
import { clearAuthTokenCookie, getAuthTokenCookie } from "../helper/authCookie";
import { getSecondApiLocale } from "../helper/apiLocale";

/** Second backend (e.g. auth) — base URL from env, same naming pattern as `Api` / `REACT_APP_API_KEY`. */
export const SecondApi = axios.create({
  baseURL: process.env.REACT_APP_SECOND_API_KEY,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const AUTH_EVENT = "nomio:auth-change";

function clearExpiredAuthSession() {
  clearAuthTokenCookie();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_EVENT));
  }
}

SecondApi.interceptors.request.use((config) => {
  const lang = localStorage.getItem("i18nextLng") || "en";
  config.headers["Accept-Language"] = getSecondApiLocale(lang);

  // Some endpoints (e.g. mobile-ticket/*) are explicitly designed for
  // anonymous, app_id-tracked access. When an authenticated browser hits
  // those routes, sending the JWT triggers backend middleware that loads
  // the wrong user model (`MobileUser` lacks the `hasPermissionTo()`
  // method) and crashes. Callers can opt out by passing `skipAuth: true`
  // in the request config. Booking mobile endpoints may still opt back into
  // account linking with `includeAuthIfAvailable: true`.
  const skipAuth = Boolean(config.skipAuth);
  const includeAuthIfAvailable = Boolean(config.includeAuthIfAvailable);
  const token =
    skipAuth && !includeAuthIfAvailable ? null : getAuthTokenCookie();
  if (token) {
    // Backend expects a custom header named `token` containing the raw token
    // (per the Swagger spec). We also keep `Authorization: Bearer <token>` for
    // compatibility with any endpoint that might use the standard scheme.
    config.headers["token"] = token;
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug(
      "[SecondApi] →",
      (config.method || "get").toUpperCase(),
      config.url,
      {
        hasToken: Boolean(token),
        tokenPreview: token ? `${token.slice(0, 6)}…${token.slice(-4)}` : null,
        skipAuth,
        includeAuthIfAvailable,
        acceptLanguage: config.headers["Accept-Language"],
      },
    );
  }
  return config;
});

SecondApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";
    if (status === 401 && String(url).includes("/auth/getUserInfo")) {
      clearExpiredAuthSession();
    }

    if (process.env.NODE_ENV !== "production") {
      const sentToken = error.config?.headers?.token ? "yes" : "no";
      // eslint-disable-next-line no-console
      console.debug("[SecondApi] ✗", status, url, {
        sentToken,
        responseData: error.response?.data,
      });
    }
    return Promise.reject(error);
  },
);
